// Prepares an LLM-authored full HTML document for safe rendering in the iframe
// sandbox. Security model (same hard boundary as the authored-component path):
//   - the iframe uses sandbox="allow-scripts" with NO allow-same-origin → opaque
//     origin: the document cannot read parent DOM, cookies, storage, or call
//     same-origin APIs.
//   - a strict CSP is injected as the FIRST element in <head> so it governs the
//     whole document: default-src 'none' (no network → nothing can be loaded from
//     or sent to a URL), only inline style/script and data: images allowed.
// The document only restructures the already-validated answer, so its content is
// non-sensitive; the sandbox + CSP exist to stop any exfiltration or escape.
//
// Pure string builder so it is fully unit-testable.

// No network of any kind (no connect-src/img URL/font URL/script src). Inline
// <style>/<script> and data: images only — enough for self-contained HTML+SVG+JS.
export const HTML_VISUAL_CSP =
  "default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; " +
  "img-src data:; font-src data:; form-action 'none'; base-uri 'none';";

const CSP_META = `<meta http-equiv="Content-Security-Policy" content="${HTML_VISUAL_CSP}">`;

// Force the document to size to its CONTENT, not the viewport. Models often write
// `html,body{height:100%}` (or min-height:100vh) for a "full page" feel; inside an
// auto-sized iframe that makes the body track the iframe's own height, which — with
// the height-report loop below — creeps the iframe up to its max and leaves a long
// blank tail. Injected after the model's styles with !important so it always wins.
const RESET_STYLE = '<style>html,body{height:auto!important;min-height:0!important;margin:0!important}</style>';

// Reports the document's CONTENT height to the parent so the iframe is sized to fit
// (the iframe has no scroll — the chat page scrolls).
//
// Avoiding the growth loop: the old reporter listened to window 'resize' and observed
// documentElement; when the parent resized the iframe, that changed the viewport and
// re-fired both, creeping the height up to the cap (a long blank tail). The fix is to
// observe document.body's CONTENT box: with the reset above, body height tracks the
// content, NOT the iframe viewport, so changing the iframe height does not change the
// observed box and never re-fires. The observer therefore stays connected safely — it
// fires only on genuine reflow (web-font swap, async layout, or a width change when
// the chat column resizes), keeping the height correct without any growth loop.
const HEIGHT_SCRIPT = `<script>(function(){
  function h(){ var b=document.body; return b?b.scrollHeight:document.documentElement.scrollHeight; }
  function post(){ parent.postMessage({__bpHtmlVisual:true,height:h()},'*'); }
  if(window.ResizeObserver){ try{ new ResizeObserver(post).observe(document.body); }catch(e){} }
  window.addEventListener('load',post);
  requestAnimationFrame(post);
  setTimeout(post,300);
})();</script>`;

const HEAD_INNER_RE = /<head\b[^>]*>([\s\S]*?)<\/head>/i;
const BODY_INNER_RE = /<body\b[^>]*>([\s\S]*?)<\/body>/i;

// Build the sandbox srcdoc from the model's (untrusted) HTML.
//
// We do NOT splice our CSP into the model's own document shell: a meta-CSP only
// governs what the parser sees AFTER it, so a <script> placed before the model's
// <head> would run unconstrained (and could reach the network). Instead we emit a
// FIXED skeleton whose first <head> child is our CSP, and re-host only the model's
// <head> and <body> *inner* content inside it. Anything the model put outside
// <head>/<body> (e.g. a leading <script>, a <base>) is dropped or, in the no-<body>
// fallback, lands in <body> AFTER the CSP — so the CSP always governs it.
export function buildHtmlSrcDoc(html: string): string {
  const src = html || '';
  const headInner = src.match(HEAD_INNER_RE)?.[1] ?? '';
  const bodyMatch = src.match(BODY_INNER_RE);
  // No explicit <body>: treat everything that isn't the <head> as body content.
  const bodyInner = bodyMatch ? bodyMatch[1] : src.replace(HEAD_INNER_RE, '');

  return (
    '<!doctype html><html><head>' +
    CSP_META +
    headInner +
    RESET_STYLE +  // after the model's styles so the content-sizing reset wins
    '</head><body>' +
    bodyInner +
    HEIGHT_SCRIPT +
    '</body></html>'
  );
}
